import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { usePagination } from '../hooks/usePagination';
import { toast } from 'react-toastify';
import { Pagination } from '../utils/Pagination';
import { formatDate } from '../utils/Config';
import { Calendar, MapPin, Clock, Plus, ArrowRight } from 'lucide-react';
import { fetchEvents } from '../redux/EventSlice';

// Event Card Component
const EventCard = ({ event }) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/dashboard/events/${event.id}`);
  };

  // Function to determine badge color based on status
  const getBadgeColor = (status) => {
    switch (status) {
      case 'upcoming':
        return 'bg-blue-100 text-blue-800';
      case 'ongoing':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
    >
      <div className="relative h-48 w-full">
        <img
          src={event.img || '/api/placeholder/400/200'}
          alt={event.title}
          className="w-full h-full object-contain"
        />
        <span className={`absolute top-4 right-4 px-3 py-1 text-xs font-medium rounded-full ${getBadgeColor(event.status)}`}>
          {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
        </span>
      </div>
      <div className="p-5">
        <h3 className="text-xl font-semibold mb-2 line-clamp-1">{event.title}</h3>
        <p className="text-gray-600 mb-4 text-sm line-clamp-2">{event.description}</p>
        
        <div className="space-y-2 text-sm text-gray-500">
          <div className="flex items-center">
            <Calendar size={16} className="mr-2" />
            <span>{formatDate(event.start_date)}{event.end_date !== event.start_date ? ` - ${formatDate(event.end_date)}` : ''}</span>
          </div>
          <div className="flex items-center">
            <Clock size={16} className="mr-2" />
            <span>{event.time}</span>
          </div>
          <div className="flex items-center">
            <MapPin size={16} className="mr-2" />
            <span className="line-clamp-1">{event.location}</span>
          </div>
        </div>
        
        <div className="mt-4 flex justify-end">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/dashboard/events/${event.id}`);
            }}
            className="flex items-center text-blue-500 hover:text-blue-700 text-sm font-medium"
          >
            View Details
            <ArrowRight size={16} className="ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Filter Component
const EventFilter = ({ onFilterChange, currentFilter }) => {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {['all', 'upcoming', 'ongoing', 'completed'].map(status => (
        <button
          key={status}
          onClick={() => onFilterChange(status)}
          className={`px-4 py-2 rounded-lg transition ${
            currentFilter === status 
              ? 'bg-[#1330C5] text-white' 
              : 'bg-gray-100 hover:bg-[#1330C5] hover:text-white'
          }`}
        >
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </button>
      ))}
    </div>
  );
};

// Main Events Page
const EventsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const events = useSelector(state => state.events.items);
  const {
    loading,
    error,
    meta
  } = useSelector((state) => state.events);
  
  const {
    pagination,
    handlePageChange,
    updatePaginationData,
    getPageNumbers,
    getItemRange
  } = usePagination({
    initialPage: meta.current_page || 1,
    initialPerPage: meta.per_page || 12
  });

  const [currentFilter, setCurrentFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    handleFetchEvents();
  }, [pagination.currentPage, currentFilter]); 

  const handleFetchEvents = () => {
    const activeFilters = {};
    if (currentFilter !== 'all') {
      activeFilters.status = currentFilter;
    }

    dispatch(fetchEvents({
      page: pagination.currentPage,
      limit: pagination.perPage,
      activeFilters,
      search: searchTerm
    }))
      .unwrap()
      .then(response => {
        if (response.meta) {
          updatePaginationData({
            current_page: response.meta.current_page,
            last_page: response.meta.last_page,
            per_page: response.meta.per_page,
            total: response.meta.total
          });
        }
      })
      .catch((error) => {
        toast.error(error.message || 'Failed to load events');
      });
  };

  const handleFilterChange = (newFilter) => {
    setCurrentFilter(newFilter);
    handlePageChange(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    handlePageChange(1);
    handleFetchEvents();
  };

  return (
    <div className="sm:p-6">
      <div className="flex flex-col sm:flex-row items-start justify-between mb-6 gap-4">
        <h1 className="text-2xl font-semibold">Events</h1>
        <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
          <form onSubmit={handleSearch} className="flex w-full sm:w-auto">
            <input
              type="text"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
            />
            <button
              type="submit"
              className="bg-[#1330C5] text-white px-4 py-2 rounded-r-lg hover:opacity-90"
            >
              Search
            </button>
          </form>
          <button
            onClick={() => navigate('/dashboard/create-event')}
            className="flex items-center justify-center bg-[#1330C5] text-white px-4 py-2 rounded-lg hover:opacity-90"
          >
            <Plus size={18} className="mr-2" />
            Create Event
          </button>
        </div>
      </div>

      <div className="mb-6">
        <EventFilter 
          onFilterChange={handleFilterChange}
          currentFilter={currentFilter}
        />
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
        </div>
      ) : events.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No events found</h3>
          <p className="text-gray-600">
            {currentFilter !== 'all' 
              ? `There are no ${currentFilter} events at the moment.` 
              : 'There are no events at the moment.'}
          </p>
          <button
            onClick={() => navigate('/dashboard/create-event')}
            className="mt-4 inline-flex items-center px-4 py-2 bg-[#1330C5] text-white rounded-md hover:opacity-90"
          >
            <Plus size={18} className="mr-2" />
            Create New Event
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {events.map(event => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>
      )}

      {events.length > 0 && (
        <div className="mt-8">
          <Pagination
            currentPage={pagination.currentPage}
            lastPage={pagination.lastPage}
            total={pagination.total}
            itemRange={getItemRange()}
            pageNumbers={getPageNumbers()}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default EventsPage;